import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Trash2, Wallet, RefreshCcw, FileDown, Mail, Smartphone, Unlock, Lock, Pencil } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import HandoverForm from "@/components/HandoverForm";
import ReturnSettlement from "@/components/ReturnSettlement";
import ExpenseSheet from "@/components/ExpenseSheet";
import AdminCheckoutForm from "@/components/AdminCheckoutForm";
import ReservationProgress from "@/components/ReservationProgress";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ALL_INVENTORY_ITEMS } from "@/lib/inventory";
import { Aurora } from "@/components/motion/Aurora";

function PhotoGrid({ photos }) {
  if (!photos?.length) return <div className="text-sm text-warmgray-light">Sin fotos</div>;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {photos.map((p, i) => (
        <a key={i} href={p} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-sand bg-white">
          <img src={p} alt={`foto-${i}`} className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-warmgray-light mb-1">{label}</div>
      <div className="text-sm text-charcoal">{children}</div>
    </div>
  );
}

function InventoryView({ inventory }) {
  if (!inventory) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {ALL_INVENTORY_ITEMS.map((it) => (
        <div key={it.id} className="flex items-center justify-between text-sm bg-ivory px-3 py-2 rounded">
          <span className="text-charcoal">{it.label}</span>
          <span className={inventory[it.id] ? "text-ok font-medium" : "text-danger font-medium"}>
            {inventory[it.id] ? "✓ OK" : "✗ Falta"}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(false);
  // null = no se está editando; texto = borrador del email del cliente
  const [emailDraft, setEmailDraft] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reservations/${id}`);
      setR(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const onRefund = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reservations/${id}/refund`);
      if (data.deduplicated) {
        toast.info("La fianza ya estaba liberada o devuelta");
      } else if (data.type === "release") {
        toast.success("Preautorización liberada (sin comisión). El cliente recibirá un email.");
      } else {
        toast.success(`Fianza devuelta (${data.amount.toFixed(2)} €). El cliente recibirá un email.`);
      }
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSaveEmail = async (value) => {
    try {
      const { data } = await api.patch(`/reservations/${id}/client-email`, { client_email: value.trim() });
      setR(data);
      setEmailDraft(null);
      toast.success("Email del cliente actualizado. Los próximos envíos irán a esta dirección.");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onSaveContact = async (changes) => {
    try {
      const { data } = await api.patch(`/reservations/${id}/contact`, changes);
      setR(data);
      setPhoneDraft(null);
      toast.success("Datos de contacto actualizados");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onToggleSelfService = async (enabled) => {
    try {
      await api.patch(`/reservations/${id}/self-service`, { enabled });
      toast.success(enabled ? "Recogida autónoma activada" : "Recogida autónoma desactivada");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onConfirmSelfService = async (action) => {
    try {
      await api.post(`/reservations/${id}/self-service/confirm-${action}`);
      toast.success(action === "unlock" ? "Apertura confirmada — el cliente ya puede acceder" : "Cierre confirmado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onSendBalanceEmail = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reservations/${id}/send-balance-email`);
      toast.success(data.status === "sent" ? "Aviso de pago enviado al cliente" : "No se pudo enviar el aviso");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onChargeRental = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reservations/${id}/charge-rental`);
      toast.success(
        `Alquiler cobrado: ${Number(data.amount).toFixed(2)} € en la tarjeta guardada. El cliente recibirá un recibo por email.`
      );
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onSendContract = async () => {
    try {
      await api.post(`/reservations/${id}/send-contract`);
      toast.success("Contrato enviado a cliente y propietario");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const onDownloadContract = async () => {
    try {
      const resp = await api.get(`/reservations/${id}/contract.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrato-${r.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Axios serializes the error response as a Blob when responseType is blob,
      // so we read it back to get the FastAPI detail message
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          toast.error(JSON.parse(text).detail || "No se pudo generar el PDF");
        } catch {
          toast.error("No se pudo generar el PDF");
        }
      } else {
        toast.error(formatError(err));
      }
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Reserva eliminada");
      navigate("/admin");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/reserva/${r.code}`);
    toast.success("Enlace copiado");
  };

  if (!r)
    return (
      <div className="aurora-page relative isolate min-h-screen bg-ivory flex items-center justify-center text-warmgray">
        <Aurora variant="subtle" fixed />
        Cargando...
      </div>
    );

  const ci = r.checkin_data;
  const co = r.checkout_data;
  const rentalAmount = Number(r.total_amount || 0);
  const rentalStatus = r.rental_payment_status;
  // Con la señal cobrada, lo que queda por cobrar del alquiler es el resto.
  const signalPaid = r.signal_payment_status === "paid" ? Number(r.signal_amount || 0) : 0;
  const rentalDue = Math.max(0, rentalAmount - signalPaid);
  const rentalChargeable =
    rentalAmount > 0 && (rentalStatus === "pending" || rentalStatus === "external");
  const hasSavedCard = Boolean(r.stripe_payment_method_id);
  const RENTAL_STATUS_LABELS = {
    pending: "Pendiente de cobro",
    paid: "Cobrado",
    external: "Cobrado fuera de la plataforma",
    not_applicable: "—",
  };

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory pb-12">
      <Aurora variant="subtle" fixed />
      <header className="bg-white border-b border-sand">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-warmgray hover:text-forest-deep" data-testid="detail-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={copyLink} className="border-sand" data-testid="detail-copy-link">
              <Copy className="h-4 w-4 mr-2" /> Copiar enlace
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-danger" data-testid="detail-delete-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar reserva?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-danger hover:bg-danger-dark" data-testid="detail-delete-confirm">
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-overline">Reserva</span>
            <div className="flex items-center gap-3 mt-2">
              <h1 className="font-display text-3xl font-bold tracking-tight text-charcoal">
                {r.client_name}
              </h1>
              <span className="font-mono text-sm font-semibold text-forest-deep bg-sand-light px-2.5 py-1 rounded-md">
                {r.code}
              </span>
            </div>
            {emailDraft === null ? (
              <div className="text-sm text-warmgray mt-1 flex items-center gap-1.5">
                <span data-testid="detail-client-email">{r.client_email}</span>
                <button
                  type="button"
                  onClick={() => setEmailDraft(r.client_email || "")}
                  className="text-warmgray-light hover:text-forest-deep"
                  title="Cambiar el email del cliente"
                  data-testid="detail-edit-email-btn"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <span>·</span>
                <span data-testid="detail-client-phone">{r.client_phone}</span>
                <button
                  type="button"
                  onClick={() => setPhoneDraft(r.client_phone || "")}
                  className="text-warmgray-light hover:text-forest-deep"
                  title="Cambiar el teléfono del cliente"
                  data-testid="detail-edit-phone-btn"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <form
                className="mt-2 flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSaveEmail(emailDraft);
                }}
              >
                <Input
                  type="email"
                  required
                  autoFocus
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  className="h-9 w-72 bg-white"
                  data-testid="detail-email-input"
                />
                <Button type="submit" size="sm" className="bg-forest-deep hover:bg-forest" data-testid="detail-email-save">
                  Guardar
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setEmailDraft(null)}>
                  Cancelar
                </Button>
              </form>
            )}
            {r.checkin_data?.email && r.checkin_data.email.toLowerCase() !== (r.client_email || "").toLowerCase() && (
              <div className="text-xs text-warn-ink mt-1" data-testid="detail-checkin-email-hint">
                En el pre-checkin el cliente indicó <strong>{r.checkin_data.email}</strong>.{" "}
                <button type="button" className="underline" onClick={() => onSaveEmail(r.checkin_data.email)}>
                  Usar este email
                </button>
              </div>
            )}
            {phoneDraft !== null && (
              <form
                className="mt-2 flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSaveContact({ client_phone: phoneDraft });
                }}
              >
                <Input
                  type="tel"
                  required
                  autoFocus
                  value={phoneDraft}
                  onChange={(e) => setPhoneDraft(e.target.value)}
                  className="h-9 w-56 bg-white"
                  data-testid="detail-phone-input"
                />
                <Button type="submit" size="sm" className="bg-forest-deep hover:bg-forest" data-testid="detail-phone-save">
                  Guardar
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setPhoneDraft(null)}>
                  Cancelar
                </Button>
              </form>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-warmgray">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={Boolean(r.whatsapp_opt_in)}
                  onCheckedChange={(v) => onSaveContact({ whatsapp_opt_in: Boolean(v) })}
                  data-testid="detail-whatsapp-optin"
                />
                Avisos por WhatsApp
              </label>
              <label className="flex items-center gap-2">
                Idioma
                <select
                  value={r.locale || "es"}
                  onChange={(e) => onSaveContact({ locale: e.target.value })}
                  className="h-7 rounded border border-sand bg-white px-1"
                  data-testid="detail-locale"
                >
                  <option value="es">Castellano</option>
                  <option value="ca">Català</option>
                </select>
              </label>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-forest-deep/10 text-forest-deep border-0 hover:bg-forest-deep/10">{r.status}</Badge>
            <Badge className="bg-terracotta/15 text-terracotta border-0 hover:bg-terracotta/15">Fianza: {r.payment_status}</Badge>
            {rentalAmount > 0 && (
              <Badge
                className={`border-0 ${
                  rentalStatus === "paid"
                    ? "bg-ok/15 text-ok hover:bg-ok/15"
                    : rentalStatus === "pending"
                    ? "bg-warn/15 text-warn-ink hover:bg-warn/15"
                    : "bg-sand-light text-warmgray hover:bg-sand-light"
                }`}
                data-testid="detail-rental-badge"
              >
                Alquiler: {RENTAL_STATUS_LABELS[rentalStatus] || rentalStatus}
              </Badge>
            )}
          </div>
        </div>

        <ReservationProgress reservation={r} />

        <div className="grid lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white border border-sand rounded-xl p-5">
            <div className="text-overline mb-2">Recogida</div>
            <div className="font-display text-lg font-semibold text-charcoal">{r.pickup_date}</div>
            <div className="text-sm text-warmgray">{r.pickup_time} · {r.pickup_location}</div>
          </div>
          <div className="bg-white border border-sand rounded-xl p-5">
            <div className="text-overline mb-2">Devolución</div>
            <div className="font-display text-lg font-semibold text-charcoal">{r.return_date}</div>
            <div className="text-sm text-warmgray">{r.return_time} · {r.return_location}</div>
          </div>
          <div className="bg-white border border-sand rounded-xl p-5">
            <div className="text-overline mb-2">Importe total</div>
            <div className="font-display text-lg font-semibold text-charcoal">
              {Number(r.total_amount || 0).toFixed(2)} €
            </div>
            <div className="text-sm text-warmgray">{r.km_included_per_day} km/día incluidos</div>
            {r.signal_payment_status === "paid" && (
              <div className="text-xs text-ok mt-2 font-medium" data-testid="detail-signal-paid">
                Señal cobrada: {Number(r.signal_amount || 0).toFixed(2)} €
                {r.signal_paid_at ? ` · ${new Date(r.signal_paid_at).toLocaleDateString("es-ES")}` : ""}
                {rentalStatus === "pending" ? ` · resto ${rentalDue.toFixed(2)} € antes de la entrega` : ""}
              </div>
            )}
            {r.signal_payment_status === "pending" && Number(r.signal_amount || 0) > 0 && (
              <div className="text-xs text-warn-ink mt-2" data-testid="detail-signal-pending">
                Señal pendiente: {Number(r.signal_amount).toFixed(2)} € ({Number(r.signal_percent || 0).toFixed(0)} %)
              </div>
            )}
            {rentalStatus === "paid" && rentalAmount > 0 && (
              <div className="text-xs text-ok mt-2 font-medium">
                Alquiler cobrado{r.rental_paid_at ? ` · ${new Date(r.rental_paid_at).toLocaleDateString("es-ES")}` : ""}
              </div>
            )}
            {rentalChargeable && hasSavedCard && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={loading} variant="outline" size="sm" className="mt-2 border-forest-deep text-forest-deep" data-testid="detail-charge-rental-btn">
                    <Wallet className="h-3.5 w-3.5 mr-2" />
                    Cobrar alquiler
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      ¿Cobrar {rentalDue.toFixed(2)} € del alquiler?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Se hará el cargo en la tarjeta guardada de la fianza, sin
                      intervención del cliente. Recibirá un recibo por email y
                      el movimiento quedará registrado en finanzas.
                      {rentalStatus === "external" &&
                        " Esta reserva está marcada como cobrada fuera de la plataforma: confirma que el alquiler NO se ha cobrado ya por otra vía."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onChargeRental} className="bg-forest-deep hover:bg-forest" data-testid="detail-charge-rental-confirm">
                      Cobrar {rentalDue.toFixed(2)} €
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {(rentalStatus === "pending" || r.payment_status === "pending") && (
              <Button
                disabled={loading}
                variant="outline"
                size="sm"
                className="mt-2 border-sand text-charcoal"
                onClick={onSendBalanceEmail}
                data-testid="detail-send-balance-btn"
              >
                Enviar aviso de pago al cliente
              </Button>
            )}
            {rentalStatus === "pending" && !hasSavedCard && (
              <div className="text-xs text-warn-ink mt-2">
                Alquiler pendiente · sin tarjeta guardada. El cliente puede
                pagarlo desde su enlace de reserva.
              </div>
            )}
            {rentalStatus === "external" && !hasSavedCard && (
              <div className="text-xs text-warmgray-light mt-2">
                Alquiler cobrado fuera de la plataforma.
              </div>
            )}
          </div>
          <div className="bg-white border border-sand rounded-xl p-5">
            <div className="text-overline mb-2">Fianza</div>
            <div className="font-display text-lg font-semibold text-charcoal">{r.deposit_amount} €</div>
            {(r.payment_status === "paid" || r.payment_status === "on_hold") && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={loading} variant="outline" size="sm" className="mt-2 border-forest-deep text-forest-deep" data-testid="detail-refund-btn">
                    <RefreshCcw className="h-3.5 w-3.5 mr-2" />
                    {r.payment_status === "on_hold" ? "Liberar fianza" : "Devolver fianza"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {r.payment_status === "on_hold"
                        ? `¿Liberar la preautorización de ${r.deposit_amount} €?`
                        : `¿Devolver ${r.deposit_amount} € al cliente?`}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {r.payment_status === "on_hold"
                        ? "La retención de la tarjeta se cancela y los fondos quedan disponibles para el cliente en horas. No hay comisión de Stripe."
                        : "Se ejecutará el refund en Stripe. El cliente lo recibirá en su cuenta en 5-10 días. Esta acción no se puede deshacer."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onRefund} className="bg-forest-deep hover:bg-forest" data-testid="detail-refund-confirm">
                      {r.payment_status === "on_hold" ? "Liberar fianza" : "Devolver fianza"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {r.payment_status === "released" && (
              <div className="text-xs text-ok mt-2 font-medium">Fianza liberada</div>
            )}
            {r.payment_status === "refunded" && (
              <div className="text-xs text-ok mt-2 font-medium">Fianza devuelta</div>
            )}
            {r.payment_status === "pending" && (
              <div className="text-xs text-warmgray-light mt-2 flex items-center gap-1">
                <Wallet className="h-3.5 w-3.5" /> Pendiente de pago por el cliente
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-sand rounded-xl p-5 mb-6" data-testid="detail-selfservice-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-forest-deep" />
              <div>
                <div className="font-display font-semibold text-charcoal">Recogida y devolución autónomas</div>
                <div className="text-xs text-warmgray-light">
                  El cliente lo hace todo desde su móvil junto a la furgoneta; tú
                  solo abres y cierras con tu app We Connect cuando te llegue el aviso.
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={Boolean(r.self_service)}
                onCheckedChange={(v) => onToggleSelfService(Boolean(v))}
                data-testid="detail-selfservice-toggle"
              />
              <span>{r.self_service ? "Activada" : "Desactivada"}</span>
            </label>
          </div>

          {(r.self_pickup || r.self_return) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {r.self_pickup && (
                <div className="bg-ivory border border-sand rounded-lg p-4" data-testid="detail-selfpickup-state">
                  <div className="text-overline mb-1">Recogida</div>
                  <div className="text-sm text-charcoal font-medium mb-1">
                    {r.self_pickup.status === "unlock_requested" && "🔓 Apertura solicitada — abre con We Connect"}
                    {r.self_pickup.status === "unlocked" && "Abierta — cliente registrando el interior"}
                    {r.self_pickup.status === "completed" && "✅ Completada"}
                  </div>
                  {r.self_pickup.gps && (
                    <div className="text-xs text-warmgray">
                      Cliente a {Math.round(r.self_pickup.gps.distance_m)} m del punto
                      (GPS ±{Math.round(r.self_pickup.gps.accuracy)} m) ·{" "}
                      {(r.self_pickup.photos_before || []).length} fotos previas
                    </div>
                  )}
                  {r.self_pickup.status === "unlock_requested" && (
                    <Button
                      size="sm"
                      onClick={() => onConfirmSelfService("unlock")}
                      className="mt-3 bg-forest-deep hover:bg-forest text-white"
                      data-testid="detail-confirm-unlock-btn"
                    >
                      <Unlock className="h-3.5 w-3.5 mr-2" /> He abierto la furgoneta
                    </Button>
                  )}
                </div>
              )}
              {r.self_return && (
                <div className="bg-ivory border border-sand rounded-lg p-4" data-testid="detail-selfreturn-state">
                  <div className="text-overline mb-1">Devolución</div>
                  <div className="text-sm text-charcoal font-medium mb-1">
                    {r.self_return.status === "lock_requested" && "🔒 Pendiente de cierre — ciérrala con We Connect"}
                    {r.self_return.status === "locked" && "✅ Cerrada"}
                  </div>
                  {r.self_return.gps && (
                    <div className="text-xs text-warmgray">
                      Devuelta a {Math.round(r.self_return.gps.distance_m)} m del punto
                    </div>
                  )}
                  {r.self_return.status === "lock_requested" && (
                    <Button
                      size="sm"
                      onClick={() => onConfirmSelfService("lock")}
                      className="mt-3 bg-forest-deep hover:bg-forest text-white"
                      data-testid="detail-confirm-lock-btn"
                    >
                      <Lock className="h-3.5 w-3.5 mr-2" /> He cerrado la furgoneta
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {(r.return_notices?.length > 0 || r.return_checklist) && (
          <div className="mb-6 space-y-3" data-testid="detail-returnprep">
            {(r.return_notices || []).map((n) => (
              <div key={n.id} className="bg-terracotta/10 border border-terracotta/30 rounded-xl px-4 py-3 text-sm text-terracotta-ink flex flex-wrap justify-between gap-2">
                <span>
                  <strong>
                    {n.kind === "delay" ? "⏰ Aviso de retraso" : n.kind === "extension" ? "📅 Solicita ampliar el alquiler" : n.kind === "incident" ? "🔧 Incidencia o daño comunicado" : "💬 Aviso del viajero"}
                  </strong>
                  {n.estimated_time ? ` · llegada estimada ${n.estimated_time}` : ""}
                  {n.extra_days ? ` · +${n.extra_days} día${n.extra_days > 1 ? "s" : ""}` : ""}
                  {n.message ? ` — ${n.message}` : ""}
                </span>
                <span className="text-xs text-warn-ink">{new Date(n.created_at).toLocaleString("es-ES")}</span>
              </div>
            ))}
            {r.return_checklist && (
              <div className="bg-white border border-sand rounded-xl px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2">
                <span>
                  <strong>Checklist de devolución del cliente:</strong>{" "}
                  {r.return_checklist.completed_at
                    ? `completada el ${new Date(r.return_checklist.completed_at).toLocaleString("es-ES")}`
                    : `${Object.values(r.return_checklist.checked || {}).filter(Boolean).length} tareas marcadas (en curso)`}
                  {r.return_checklist.notes ? ` · Notas: “${r.return_checklist.notes}”` : ""}
                </span>
              </div>
            )}
          </div>
        )}
        {!co && r.handover_data && (
          <div className="mb-6 flex flex-wrap items-center gap-3 text-xs text-warmgray-light">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const { data } = await api.post(`/admin/reservations/${r.id}/return-prep/test-email`);
                  toast.success(`Email de preparación enviado como PRUEBA a ${data.sent_to}`);
                } catch (err) {
                  toast.error(formatError(err));
                }
              }}
              data-testid="detail-returnprep-test-email"
            >
              Enviar email de preparación (prueba a tu buzón)
            </Button>
            <span>Solo va al buzón del propietario; el cliente no recibe nada.</span>
          </div>
        )}
        <Tabs defaultValue="checkin">
          <TabsList className="bg-sand-light">
            <TabsTrigger value="checkin" data-testid="tab-checkin">Pre-Checkin</TabsTrigger>
            <TabsTrigger value="handover" data-testid="tab-handover">Entrega</TabsTrigger>
            <TabsTrigger value="checkout" data-testid="tab-checkout">Devolución</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Gastos</TabsTrigger>
            <TabsTrigger value="settlement" data-testid="tab-settlement">Liquidación</TabsTrigger>
            <TabsTrigger value="contract" data-testid="tab-contract">Contrato</TabsTrigger>
          </TabsList>

          <TabsContent value="checkin" className="mt-4">
            {!ci ? (
              <div className="bg-white border border-sand rounded-xl p-8 text-center text-warmgray-light">
                El cliente aún no ha realizado el pre-checkin.
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white border border-sand rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Datos del viajero</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nombre">{ci.full_name}</Field>
                    <Field label="DNI / NIE">{ci.dni_number}</Field>
                    <Field label="Carnet conducir">{ci.license_number}</Field>
                    <Field label="Carnet obtenido el">{ci.license_issue_date || "—"}</Field>
                    <Field label="Fecha nacimiento">{ci.birth_date}</Field>
                    <Field label="Dirección">{ci.address}</Field>
                    <Field label="Teléfono">{ci.phone || r.client_phone}</Field>
                    <Field label="Email">{ci.email || r.client_email}</Field>
                    <Field label="Países a visitar">{ci.countries_to_visit || "—"}</Field>
                  </div>
                  {(ci.dni_photo_front || ci.dni_photo_back || ci.license_photo) && (
                    <div className="grid sm:grid-cols-3 gap-3 mt-5">
                      {ci.dni_photo_front && <a href={ci.dni_photo_front} target="_blank" rel="noreferrer"><img src={ci.dni_photo_front} alt="DNI delante" className="w-full rounded-lg border border-sand" /></a>}
                      {ci.dni_photo_back && <a href={ci.dni_photo_back} target="_blank" rel="noreferrer"><img src={ci.dni_photo_back} alt="DNI detrás" className="w-full rounded-lg border border-sand" /></a>}
                      {ci.license_photo && <a href={ci.license_photo} target="_blank" rel="noreferrer"><img src={ci.license_photo} alt="Carnet" className="w-full rounded-lg border border-sand" /></a>}
                    </div>
                  )}
                </div>

                {ci.additional_driver && (
                  <div className="bg-white border border-sand rounded-xl p-6" data-testid="detail-additional-driver">
                    <h3 className="font-display text-lg font-semibold text-charcoal mb-4">
                      Segundo conductor <span className="text-xs font-normal text-ok">(sin coste)</span>
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Nombre">{ci.additional_driver.full_name}</Field>
                      <Field label="DNI / NIE">{ci.additional_driver.dni_number}</Field>
                      <Field label="Carnet conducir">{ci.additional_driver.license_number}</Field>
                      <Field label="Carnet obtenido el">{ci.additional_driver.license_issue_date || "—"}</Field>
                      <Field label="Fecha nacimiento">{ci.additional_driver.birth_date || "—"}</Field>
                    </div>
                    {(ci.additional_driver.dni_photo_front || ci.additional_driver.dni_photo_back || ci.additional_driver.license_photo) && (
                      <div className="grid sm:grid-cols-3 gap-3 mt-5">
                        {ci.additional_driver.dni_photo_front && <a href={ci.additional_driver.dni_photo_front} target="_blank" rel="noreferrer"><img src={ci.additional_driver.dni_photo_front} alt="DNI 2º conductor delante" className="w-full rounded-lg border border-sand" /></a>}
                        {ci.additional_driver.dni_photo_back && <a href={ci.additional_driver.dni_photo_back} target="_blank" rel="noreferrer"><img src={ci.additional_driver.dni_photo_back} alt="DNI 2º conductor detrás" className="w-full rounded-lg border border-sand" /></a>}
                        {ci.additional_driver.license_photo && <a href={ci.additional_driver.license_photo} target="_blank" rel="noreferrer"><img src={ci.additional_driver.license_photo} alt="Carnet 2º conductor" className="w-full rounded-lg border border-sand" /></a>}
                      </div>
                    )}
                  </div>
                )}

                {(r.selected_items || []).length > 0 && (
                  <div className="bg-white border border-sand rounded-xl p-6">
                    <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Mejoras y extras contratados</h3>
                    <div className="space-y-2">
                      {r.selected_items.map((it) => (
                        <div key={it.product_id} className="flex items-center justify-between text-sm bg-ivory px-4 py-2.5 rounded-lg">
                          <div>
                            <span className="text-charcoal font-medium">{it.name}</span>
                            {it.qty > 1 && <span className="text-warmgray-light"> ×{it.qty}</span>}
                            <span className="text-xs text-warmgray-light ml-2">
                              ({it.price_type === "per_day" ? `${it.unit_price} €/día` : `${it.unit_price} €`})
                            </span>
                          </div>
                          <span className={`font-semibold ${it.subtotal > 0 ? "text-forest-deep" : "text-warmgray-light"}`}>
                            {it.subtotal > 0 ? `${it.subtotal.toFixed(2)} €` : "Incluido"}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-sm px-4 pt-2 border-t border-sand">
                        <span className="font-medium text-charcoal">Total extras</span>
                        <div className="text-right">
                          <span className="font-display font-bold text-forest-deep">{Number(r.extras_amount || 0).toFixed(2)} €</span>
                          <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${r.extras_payment_status === "paid" ? "bg-ok/15 text-ok" : "bg-warn/15 text-warn-ink"}`}>
                            {r.extras_payment_status === "paid" ? "Pagado" : "Pendiente"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {ci.signature && (
                  <div className="bg-white border border-sand rounded-xl p-6">
                    <h3 className="font-display text-lg font-semibold text-charcoal mb-2">Firma del viajero</h3>
                    <img src={ci.signature} alt="Firma" className="max-w-sm border border-sand rounded-lg" />
                    <div className="text-xs text-warmgray-light mt-2">Firmado el {new Date(ci.submitted_at).toLocaleString("es-ES")}</div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="expenses" className="mt-4">
            {!r.checkout_data ? (
              <div className="bg-white border border-sand rounded-xl p-8 text-center text-warmgray-light">
                El control de gastos estará disponible tras registrar la devolución del vehículo.
              </div>
            ) : (
              <div className="bg-white border border-sand rounded-xl p-6">
                <h3 className="font-display text-lg font-semibold text-charcoal mb-1">Control de gastos</h3>
                <p className="text-sm text-warmgray mb-5">
                  Presupuesto de cargos de la devolución: reparaciones, km adicionales,
                  combustible y tasa de repostaje. Envíalo al viajero cuando esté completo;
                  al liquidar la fianza podrás importar estas líneas.
                </p>
                <ExpenseSheet reservation={r} onSaved={load} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="settlement" className="mt-4">
            {r.deposit_settled && r.settlement ? (
              <div className="bg-white border border-sand rounded-xl p-6" data-testid="settlement-done">
                <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Fianza liquidada</h3>
                <div className="space-y-2 max-w-lg">
                  {(r.settlement.charges || []).map((c, i) => (
                    <div key={i} className="flex justify-between text-sm bg-ivory px-4 py-2.5 rounded-lg">
                      <span className="text-charcoal">{c.concept}</span>
                      <span className="font-semibold text-danger">{Number(c.amount).toFixed(2)} €</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm px-4 pt-2 border-t border-sand">
                    <span className="font-medium">Total retenido</span>
                    <span className="font-display font-bold text-danger">
                      {Number(r.settlement.total_charges || 0).toFixed(2)} €
                    </span>
                  </div>
                  <div className="flex justify-between text-sm px-4">
                    <span className="font-medium">Liberado / devuelto al cliente</span>
                    <span className="font-display font-bold text-ok">
                      {Math.max(0, Number(r.settlement.deposit_amount || 0) - Number(r.settlement.total_charges || 0)).toFixed(2)} €
                    </span>
                  </div>
                  <div className="text-xs text-warmgray-light px-4 pt-1">
                    Liquidada por {r.settlement.settled_by} · {new Date(r.settlement.settled_at).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>
            ) : !r.handover_data ? (
              <div className="bg-white border border-sand rounded-xl p-8 text-center text-warmgray-light">
                La liquidación estará disponible tras registrar la entrega del vehículo.
              </div>
            ) : (
              <ReturnSettlement reservation={r} onSaved={load} />
            )}
          </TabsContent>

          <TabsContent value="handover" className="mt-4">
            {!ci ? (
              <div className="bg-white border border-sand rounded-xl p-8 text-center text-warmgray-light">
                Espera a que el cliente complete el Pre-Checkin antes de la entrega.
              </div>
            ) : r.handover_data ? (
              <div className="space-y-6">
                <div className="bg-white border border-sand rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Entrega registrada</h3>
                  <div className="grid sm:grid-cols-4 gap-4 mb-3">
                    <Field label="Km salida">{r.handover_data.km_start}</Field>
                    <Field label="Combustible">{r.handover_data.fuel_level}</Field>
                    <Field label="Agua limpia">{r.handover_data.fresh_water_level}</Field>
                    <Field label="Neumáticos">{r.handover_data.tire_state || "—"}</Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <Field label="Aguas grises">{r.handover_data.grey_water_emptied ? "Vaciadas" : "NO vaciadas"}</Field>
                    <Field label="Aguas negras">{r.handover_data.black_water_emptied ? "Vaciadas" : "NO vaciadas"}</Field>
                  </div>
                  {r.handover_data.damage_notes && (
                    <Field label="Daños previos">{r.handover_data.damage_notes}</Field>
                  )}
                  {r.handover_data.additional_driver && (
                    <div className="mt-4 pt-4 border-t border-sand">
                      <div className="text-overline mb-2">Conductor adicional</div>
                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <Field label="Nombre">{r.handover_data.additional_driver.full_name}</Field>
                        <Field label="DNI">{r.handover_data.additional_driver.dni_number}</Field>
                        <Field label="Carnet">{r.handover_data.additional_driver.license_number}</Field>
                        <Field label="Obtenido">{r.handover_data.additional_driver.license_issue_date || "—"}</Field>
                      </div>
                    </div>
                  )}
                  <div className="mt-4">
                    <PhotoGrid photos={r.handover_data.photos} />
                  </div>
                  {r.handover_data.damage_photos?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-overline mb-2">Fotos daños previos</div>
                      <PhotoGrid photos={r.handover_data.damage_photos} />
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {r.handover_data.traveler_signature && (
                    <div className="bg-white border border-sand rounded-xl p-4">
                      <div className="text-overline mb-2">Firma viajero</div>
                      <img src={r.handover_data.traveler_signature} alt="firma viajero" className="max-w-full border border-sand rounded-lg" />
                    </div>
                  )}
                  {r.handover_data.owner_signature && (
                    <div className="bg-white border border-sand rounded-xl p-4">
                      <div className="text-overline mb-2">Firma propietario</div>
                      <img src={r.handover_data.owner_signature} alt="firma propietario" className="max-w-full border border-sand rounded-lg" />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <HandoverForm reservation={r} onSaved={load} />
            )}
          </TabsContent>

          <TabsContent value="checkout" className="mt-4">
            {!co ? (
              !r.handover_data ? (
                <div className="bg-white border border-sand rounded-xl p-8 text-center text-warmgray-light">
                  La devolución estará disponible tras registrar la entrega del vehículo.
                </div>
              ) : (
                <AdminCheckoutForm reservation={r} onSaved={load} />
              )
            ) : (
              <div className="space-y-6">
                <div className="bg-white border border-sand rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold text-charcoal mb-4">Estado del vehículo · Regreso</h3>
                  <div className="grid sm:grid-cols-4 gap-4 mb-3">
                    <Field label="Km llegada">{co.km_end}</Field>
                    <Field label="Km recorridos">{co.km_total ?? "—"}</Field>
                    <Field label="Km extra">{co.km_extra ?? 0}</Field>
                    <Field label="Combustible">{co.fuel_level}</Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    <Field label="Limp. interior">{co.interior_clean ? "OK" : "Insatisfactorio"}</Field>
                    <Field label="Limp. exterior">{co.exterior_clean ? "OK" : "Insatisfactorio"}</Field>
                    <Field label="Devuelto a tiempo">{co.returned_on_time ? "Sí" : "No"}</Field>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    <Field label="Aguas negras vaciadas">{co.black_water_emptied ? "Sí" : "No"}</Field>
                    <Field label="Aguas grises vaciadas">{co.grey_water_emptied ? "Sí" : "No"}</Field>
                    <Field label="Depósito agua llenado">{co.fresh_water_filled ? "Sí" : "No"}</Field>
                  </div>
                  {co.extra_charge > 0 && (
                    <div className="bg-warn/10 border border-warn/30 rounded-lg p-3 text-sm text-warn-ink mb-4">
                      Cargo por exceso de km: <strong>{co.extra_charge.toFixed(2)} €</strong>
                    </div>
                  )}
                  {co.incidents && <Field label="Incidencias">{co.incidents}</Field>}
                  {co.damages && <div className="mt-3"><Field label="Daños">{co.damages}</Field></div>}
                  <div className="mt-4">
                    <PhotoGrid photos={co.photos} />
                  </div>
                </div>

                {co.signature && (
                  <div className="bg-white border border-sand rounded-xl p-6">
                    <h3 className="font-display text-lg font-semibold text-charcoal mb-2">Firma de devolución</h3>
                    <img src={co.signature} alt="Firma" className="max-w-sm border border-sand rounded-lg" />
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contract" className="mt-4">
            <div className="bg-white border border-sand rounded-xl p-8">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <h3 className="font-display text-xl font-bold text-charcoal">Contrato de alquiler</h3>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={onSendContract}
                    disabled={!ci}
                    variant="outline"
                    size="sm"
                    className="border-forest-deep text-forest-deep"
                    data-testid="detail-contract-send-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Enviar por email
                  </Button>
                  <Button
                    onClick={onDownloadContract}
                    disabled={!ci}
                    className="bg-forest-deep hover:bg-forest text-white"
                    size="sm"
                    data-testid="detail-contract-pdf-btn"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    {ci ? "Descargar PDF" : "Tras Pre-Checkin"}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-warmgray leading-relaxed mb-4">
                Conforme al contrato Yescapa: el viajero examina el estado del
                vehículo (Volkswagen California Ocean, listing #91679),
                verifica el inventario y se compromete a devolverlo en su
                estado original a la hora y lugar convenidos.
              </p>
              <ul className="text-sm text-warmgray space-y-1 list-disc pl-5">
                <li>Importe total alquiler: {Number(r.total_amount || 0).toFixed(2)} €</li>
                <li>Fianza: {r.deposit_amount} € · cobrada digitalmente</li>
                <li>Km incluidos: {r.km_included_per_day} / día · extra a {r.extra_km_rate} €/km</li>
                <li>Limpieza interior y exterior obligatoria entre alquileres</li>
                <li>Vaciado obligatorio de aguas negras y grises antes de la devolución</li>
              </ul>
              {r.notes && (
                <div className="mt-6 pt-4 border-t border-sand">
                  <div className="text-overline mb-1">Notas internas</div>
                  <p className="text-sm text-warmgray">{r.notes}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
